import { SITE_CONFIG } from '../config';
import { showPropertyAccessError, clearPropertyAccessError } from './propertyAccessNotice';

const API = 'https://tophamtechbe.com/api/treas/idx/v1';
const READ_PATH = /^\/api\/treas\/idx\/v1\/retrieve-property-(summary(?:\/(?:page|map))?|data(?:\/[0-9]+)?|media\/[A-Za-z0-9_.-]+)$/;

export function createPropertyClient({ siteId, fetchImpl = (...args) => fetch(...args), now = Date.now, onError = () => {}, onSuccess = () => {}, maxConcurrent = 4 }) {
  if (!/^[A-Za-z0-9_.-]{1,64}$/.test(siteId)) throw new Error('Invalid property site.');
  if (!Number.isInteger(maxConcurrent) || maxConcurrent < 1) throw new Error('Invalid property concurrency.');
  let active = 0;
  const pending = [];
  const cancelled = () => new DOMException('The request was cancelled.', 'AbortError');
  // A page may need several card galleries. Keep one browser below the site's
  // concurrency limit, and cancel obsolete searches while they are still queued.
  function release() {
    active -= 1;
    const next = pending.shift();
    if (next) {
      next.signal?.removeEventListener('abort', next.abort);
      active += 1;
      next.resolve(release);
    }
  }
  function acquire(signal) {
    if (signal?.aborted) return Promise.reject(cancelled());
    if (active < maxConcurrent) { active += 1; return release; }
    return new Promise((resolve, reject) => {
      const entry = { resolve, signal, abort: () => {
        const index = pending.indexOf(entry);
        if (index >= 0) pending.splice(index, 1);
        reject(cancelled());
      } };
      pending.push(entry);
      signal?.addEventListener('abort', entry.abort, { once: true });
    });
  }
  let blockedUntil = 0;
  let blockedError = null;
  function failure(status, response) {
    const raw = response?.headers?.get('Retry-After');
    const seconds = raw && /^\d+$/.test(raw) ? Number(raw) : raw ? (Date.parse(raw) - now()) / 1000 : NaN;
    const retryAfter = Math.max(1, Number.isFinite(seconds) ? Math.ceil(seconds) : status === 429 ? 60 : 5);
    const error = new Error(status === 429
      ? `Property browsing is busy. Please try again in ${retryAfter} seconds.`
      : 'We could not load properties. Please try again shortly.');
    error.status = status;
    error.retryAfter = retryAfter;
    blockedUntil = now() + retryAfter * 1000;
    blockedError = error;
    onError(error);
    return error;
  }

  async function propertyFetch(url, options = {}, transport = fetchImpl) {
    const parsed = new URL(url, window.location.href);
    const route = parsed.origin === 'https://tophamtechbe.com' && parsed.pathname.match(READ_PATH);
    if (!route || (options.method || 'GET').toUpperCase() !== 'GET') return transport(url, options);
    if (options.signal?.aborted) throw new DOMException('The request was cancelled.', 'AbortError');
    if (now() < blockedUntil) throw blockedError;
    const target = process.env.NODE_ENV === 'development' && process.env.REACT_APP_IDX_LOCAL_PROXY === '1'
      ? `/__idx${parsed.pathname}${parsed.search}`
      : `${API}/website/${encodeURIComponent(siteId)}/${route[1]}${parsed.search}`;
    // Public gateway request: the private credential is added only inside IDX.
    // Do not send old keys, user login tokens, or cookies to this route.
    const headers = new Headers({ Accept: 'application/json' });
    const slot = acquire(options.signal);
    const done = typeof slot === 'function' ? slot : await slot;
    try {
      if (options.signal?.aborted) throw cancelled();
      if (now() < blockedUntil) throw blockedError;
      let response;
      try {
        response = await transport(target, { ...options, headers, credentials: 'omit', redirect: 'error', cache: 'no-store' });
      } catch (error) {
        if (error.name === 'AbortError') throw error;
        throw failure(503);
      }
      if ([401, 403, 429, 502, 503, 504].includes(response.status)) failure(response.status, response);
      else if (response.ok && now() >= blockedUntil) onSuccess();
      return response;
    } finally { done(); }
  }
  return { propertyFetch };
}

const client = createPropertyClient({ siteId: SITE_CONFIG.SITE_ID,
  onError: showPropertyAccessError, onSuccess: clearPropertyAccessError });
export const propertyFetch = (...args) => client.propertyFetch(...args);
